import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { api, type Me } from '../api';
import { money } from '../form';
import { errorText, useI18n } from '../i18n';
import { Alert, PageHead, PanelShell } from '../layout';
import { SendInvoiceDialog } from './send';

type Status = 'draft' | 'sent' | 'accepted' | 'rejected' | 'invoiced';
interface Row {
  id: number; series: string; number: string; issueDate: string; validUntil: string | null; status: Status; total: number; currency: string;
  buyerName: string; buyerEmail: string | null; invoiceId: number | null; invoiceLabel: string | null; expired: boolean;
}

const STATUS: Record<Status, [string, string]> = {
  draft: ['Juodraštis', 'draft'], sent: ['Išsiųstas', 'sent'], accepted: ['Priimtas', 'paid'], rejected: ['Atmestas', 'cancelled'], invoiced: ['Išrašyta sąskaita', 'paid'],
};
const FILTERS: ['' | Status, string][] = [['', 'Visi'], ['draft', 'Juodraščiai'], ['sent', 'Išsiųsti'], ['accepted', 'Priimti'], ['rejected', 'Atmesti'], ['invoiced', 'Išrašytos sąskaitos']];

/** Pasiūlymai: quotes to customers — sent, accepted or not, and the ones that became invoices. */
export function QuotesPage({ me }: { me: Me }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<'' | Status>('');
  const [sending, setSending] = useState<{ id: number; label: string; email?: string } | null>(null);
  const list = useQuery({ queryKey: ['quotes', filter], queryFn: () => api.get<{ quotes: Row[] }>(`api/v1/quotes${filter ? `?status=${filter}` : ''}`).then((r) => r.quotes) });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['quotes'] });
  const status = useMutation({ mutationFn: ({ id, to }: { id: number; to: Status }) => api.post(`api/v1/quotes/${id}/status`, { status: to }), onSuccess: refresh });
  const remove = useMutation({ mutationFn: (id: number) => api.delete(`api/v1/quotes/${id}`), onSuccess: refresh });
  const rows = list.data ?? [];
  const error = status.error ?? remove.error;
  return (
    <PanelShell me={me} head={<PageHead eyebrow={t('Sąskaitos')} title={t('Pasiūlymai')} text={t('Komerciniai pasiūlymai klientams. Priimtą pasiūlymą vienu paspaudimu paverskite sąskaita.')}
      action={<Link className="button button--primary" to="/quotes/new">+ {t('Naujas pasiūlymas')}</Link>} />}>
      {error ? <Alert kind="error">{errorText(t, error)}</Alert> : null}
      <nav className="project-tabs page-tabs">
        {FILTERS.map(([key, label]) => <button key={key || 'all'} type="button" className={filter === key ? 'active' : ''} onClick={() => setFilter(key)}>{t(label)}</button>)}
      </nav>
      <section className="panel-card">
        <div className="table-wrap"><table className="invoice-table quote-table">
          <thead><tr><th>{t('Nr.')}</th><th>{t('Klientas')}</th><th>{t('Data')}</th><th>{t('Būsena')}</th><th className="right">{t('Suma')}</th><th /></tr></thead>
          <tbody>
            {list.isSuccess && !rows.length ? <tr><td colSpan={6} className="empty">{t('Pasiūlymų dar nėra.')}</td></tr> : null}
            {rows.map((r) => {
              const label = `${r.series} ${r.number}`.trim();
              const [name, badge] = STATUS[r.status];
              return (
                <tr key={r.id}>
                  <td><Link to="/quotes/$id" params={{ id: String(r.id) }}><strong>{label}</strong></Link></td>
                  <td>{r.buyerName}</td>
                  <td>{r.issueDate}{r.validUntil ? <small className={r.expired ? 'text-late' : undefined}>{t('galioja iki {date}', { date: r.validUntil })}</small> : null}</td>
                  <td><span className={`badge badge--${badge}`}>{t(name)}</span>
                    {r.expired ? <small className="text-late">{t('Galiojimas baigėsi')}</small> : null}
                    {r.invoiceId ? <small><Link to="/outgoing/$id" params={{ id: String(r.invoiceId) }}>{r.invoiceLabel}</Link></small> : null}</td>
                  <td className="right"><strong>{money(r.total, r.currency)}</strong></td>
                  <td className="row-actions">
                    <Link to="/view" search={{ type: 'quote', id: r.id, title: label }}>{t('Peržiūrėti')}</Link>
                    <button type="button" className="text-link" onClick={() => setSending({ id: r.id, label, ...(r.buyerEmail ? { email: r.buyerEmail } : {}) })}>{t('Siųsti')}</button>
                    {r.status === 'sent' || r.status === 'draft' ? <>
                      <button type="button" className="text-link" disabled={status.isPending} onClick={() => status.mutate({ id: r.id, to: 'accepted' })}>{t('Priimtas')}</button>
                      <button type="button" className="text-link" disabled={status.isPending} onClick={() => status.mutate({ id: r.id, to: 'rejected' })}>{t('Atmestas')}</button>
                    </> : null}
                    {r.status === 'accepted' || r.status === 'rejected' ? <button type="button" className="text-link" disabled={status.isPending} onClick={() => status.mutate({ id: r.id, to: 'sent' })}>{t('Grąžinti')}</button> : null}
                    {r.status !== 'invoiced' ? <Link to="/outgoing/new" search={{ quote: r.id }}>{t('→ Sąskaita')}</Link> : null}
                    {r.status !== 'invoiced' ? <button type="button" className="text-danger" disabled={remove.isPending}
                      onClick={() => { if (confirm(t('Ištrinti pasiūlymą {n}?', { n: label }))) remove.mutate(r.id); }}>{t('Ištrinti')}</button> : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table></div>
      </section>
      <SendInvoiceDialog invoice={sending} onClose={() => setSending(null)} quote />
    </PanelShell>
  );
}
